/**
 * Service-worker registration + update flow (offline-first production shell).
 *
 * DEV GUARANTEE (`npm run dev` never poisoned):
 *   1. `import.meta.env.DEV` is statically `true` under the Vite dev server,
 *      so this module NEVER calls register() there — instead it actively
 *      unregisters any worker already on the origin (a stale one could only
 *      exist if something served a build on the same host:port earlier).
 *      In the production bundle the same flag is `false` and the unregister
 *      branch is dead-code-eliminated.
 *   2. `/sw.js` does not exist in dev anyway: it is generated into `dist/` at
 *      build time and `public/` contains no `sw.js`, so the dev server would
 *      answer 404 even if something asked for it.
 *
 * UPDATE FLOW (nothing ever reloads the page on its own):
 *   an installed-but-waiting worker (a new deploy) surfaces as
 *   `state.waiting` → `UpdateNotice` renders the dismissible bilingual pill →
 *   only a click on it calls `applySwUpdate()`, which posts SKIP_WAITING to
 *   the waiting worker; the reload happens on `controllerchange`, gated by
 *   the flag that click just set (the first install's clients.claim() also
 *   fires controllerchange and must NOT bounce the page).
 */
import { useSyncExternalStore } from 'react';

export interface SwUpdateState {
  /** Newly installed worker waiting to take over — null when nothing to apply. */
  waiting: ServiceWorker | null;
  /** The GM dismissed the notice this session; they are never nag-reloaded. */
  dismissed: boolean;
}

const IDLE: SwUpdateState = { waiting: null, dismissed: false };

let state: SwUpdateState = IDLE;
const listeners = new Set<() => void>();

/** True only after the GM clicked the update notice. */
let updateRequested = false;
let reloaded = false;

function setState(patch: Partial<SwUpdateState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): SwUpdateState {
  return state;
}

/** Update state as React sees it (same useSyncExternalStore pattern as print-target). */
export function useSwUpdate(): SwUpdateState {
  return useSyncExternalStore(subscribe, getSnapshot, () => IDLE);
}

/** Explicit user action only: activate the waiting worker, then reload. */
export function applySwUpdate(): void {
  const waiting = state.waiting;
  if (!waiting) return;
  updateRequested = true;
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/** Hide the notice for this session — the worker keeps waiting; no reload. */
export function dismissSwUpdate(): void {
  setState({ dismissed: true });
}

/** Register `/sw.js` — production only (see the docstring above for dev). */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  if (import.meta.env.DEV) {
    // Dev must not be poisoned: never register, drop anything stale.
    void navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((reg) => reg.unregister())))
      .catch(() => undefined);
    return;
  }

  // Reload only when the GM asked for the update: on the very first install
  // clients.claim() fires controllerchange too, and bouncing then would
  // interrupt a page nobody asked to refresh.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!updateRequested || reloaded) return;
    reloaded = true;
    window.location.reload();
  });

  const track = (registration: ServiceWorkerRegistration): void => {
    const watchInstalling = (): void => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state !== 'installed') return;
        // A controller exists only for an UPDATE (a first install has none):
        // that is exactly the "new version waiting" case.
        if (navigator.serviceWorker.controller) {
          setState({ waiting: registration.waiting ?? installing, dismissed: false });
        }
      });
    };
    watchInstalling();
    registration.addEventListener('updatefound', watchInstalling);

    // An update that finished while the tab was closed is already waiting.
    if (registration.waiting && navigator.serviceWorker.controller) {
      setState({ waiting: registration.waiting, dismissed: false });
    }

    // A GM session can outlive a deploy: ask for a fresh worker hourly (one
    // tiny /sw.js fetch; while offline it simply fails and is swallowed).
    window.setInterval(() => {
      void registration.update().catch(() => undefined);
    }, 60 * 60 * 1000);
  };

  const start = (): void => {
    // The build's own base: '/' at the domain root, '/atla-gm-site/' in a
    // subfolder — the worker and its scope must move with the deployment.
    const base = import.meta.env.BASE_URL;
    navigator.serviceWorker
      .register(`${base}sw.js`, { scope: base, updateViaCache: 'none' })
      .then(track)
      .catch(() => undefined); // offline / unsupported → app works as today
  };

  // Defer past first paint — never compete with the shell load on a slow
  // convention wifi.
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}
